import React, { useEffect } from 'react';
import { useWorker } from '@koale/useworker';
import { useDispatch, useSelector } from '../../stores';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Footer } from './Footer';
import { MenuBars } from './MenuBars';

import { salesListSlice, fetchConsultSalesExportCommissions } from '../../stores';
import {
  exportSalesCommissionsToHTML,
  exportSalesToHTML,
  handleApiDataSalesCommissionsToHTMLPartOne,
  handleApiDataSalesCommissionsToHTMLPartTwo,
  handleApiDataSalesCommissionsToHTMLPartThree,
  handleApiDataSalesToHTMLPartOne,
  handleApiDataSalesToHTMLPartTwo,
  handleApiDataSalesToHTMLPartThree,
  handleApiDataSalesToHTMLPartFour,
} from '../../pages/SalesManagement/table/exports';

import { StyledOuterContainer, StyledInnerContainer } from './styles';
import useResponsiveMode from '../../utils/useResponsiveMode';

export interface PageWrapperProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const PageWrapper = (props: PageWrapperProps) => {
  const { children, style } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const responsiveMode = useResponsiveMode();
  const dispatch = useDispatch();

  const { profileId } = useSelector((state) => state.authReducer);
  const { salesReportId } = useSelector((state) => state.salesListReducer);
  const salesFilters = useSelector((state) => state.salesFiltersReducer.filters);
  const salesFiltersValues = useSelector((state) => state.salesFiltersValuesReducer);

  const [exportSalesCommissionsToHTMLPartOneWorker] = useWorker(
    handleApiDataSalesCommissionsToHTMLPartOne,
  );
  const [exportSalesCommissionsToHTMLPartTwoWorker] = useWorker(
    handleApiDataSalesCommissionsToHTMLPartTwo,
  );
  const [exportSalesCommissionsToHTMLPartThreeWorker] = useWorker(
    handleApiDataSalesCommissionsToHTMLPartThree,
  );
  const [exportSalesToHTMLPartOneWorker] = useWorker(
    handleApiDataSalesToHTMLPartOne,
  );
  const [exportSalesToHTMLPartTwoWorker] = useWorker(
    handleApiDataSalesToHTMLPartTwo,
  );
  const [exportSalesToHTMLPartThreeWorker] = useWorker(
    handleApiDataSalesToHTMLPartThree,
  );
  const [exportSalesToHTMLPartFourWorker] = useWorker(
    handleApiDataSalesToHTMLPartFour,
  );

  useEffect(() => {
    if (+profileId == 10) {
      if (location.pathname != '/consulta-veiculo') {
        navigate('/consulta-veiculo');
      }
    }
  }, [profileId]);

  useEffect(() => {
    if (!salesReportId) {
      return;
    }

    const startTime = Date.now();
    const maxDurationMs = 5 * 60 * 1000;
    let isActive = true;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }

    const handleSuccess = async (response: any) => {
      if (!isActive) {
        return;
      }

      const reportTypeId = response?.data?.reportTypeId;
      const report = response?.data?.report;

      if (!report || !reportTypeId) {
        return;
      }

      stopPolling();
      toast.dismiss('async-report');

      dispatch(salesListSlice.actions.clearSalesReportId());
      dispatch(salesListSlice.actions.toggleExportModalIsOpen());

      try {
        if (reportTypeId == 1) {
          await exportSalesCommissionsToHTML(
            report,
            salesFiltersValues?.startDateTime ?? '',
            salesFiltersValues?.finalDateTime ?? '',
            dispatch,
            exportSalesCommissionsToHTMLPartOneWorker,
            exportSalesCommissionsToHTMLPartTwoWorker,
            exportSalesCommissionsToHTMLPartThreeWorker
          );
        }

        if (
          reportTypeId == 2 &&
          salesFilters?.users &&
          salesFilters?.areas &&
          salesFilters?.routes
        ) {
          await exportSalesToHTML(
            report,
            salesFilters.users,
            salesFilters.areas,
            salesFilters.routes,
            dispatch,
            exportSalesToHTMLPartOneWorker,
            exportSalesToHTMLPartTwoWorker,
            exportSalesToHTMLPartThreeWorker,
            exportSalesToHTMLPartFourWorker
          );
        }
      } finally {
        dispatch(salesListSlice.actions.toggleExportModalIsOpen());
      }
    }

    const attemptFetch = () => {
      dispatch(fetchConsultSalesExportCommissions({ reportId: +salesReportId }))
        .unwrap()
        .then((res) => handleSuccess(res))
        .catch(() => undefined);
    }

    intervalId = setInterval(() => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= maxDurationMs) {
        stopPolling();
        toast.dismiss('async-report');
        dispatch(salesListSlice.actions.clearSalesReportId());
        return;
      }

      attemptFetch();
    }, 15000);

    timeoutId = setTimeout(() => {
      stopPolling();
      toast.dismiss('async-report');
      dispatch(salesListSlice.actions.clearSalesReportId());
    }, maxDurationMs);

    attemptFetch();

    return () => {
      isActive = false;
      stopPolling();
    }
  }, [salesReportId]);

  return (
    <StyledOuterContainer
      style={{ ...style, overflow: 'overlay' }}
      sx={{
        paddingLeft:
          profileId === 9 || responsiveMode === 'phone' ? '0px' : '62px',
      }}
    >
      <StyledInnerContainer
        style={style}
        sx={{
          paddingLeft: responsiveMode === 'phone' ? '10px' : '30px',
          paddingRight: responsiveMode === 'phone' ? '10px' : '30px',
        }}
      >
        <MenuBars />
        {children}
      </StyledInnerContainer>
      <Footer />
    </StyledOuterContainer>
  );
};